import ShoppingCart from '@/assets/icons/ShoppingCart.svg';
import { useAddToBasket } from '@/hooks/useAddToBasket';
import type { Product } from '@/types';

interface ProductAddToBasketButtonData {
  code: string;
  quantity?: number;
  label: string;
  className?: string;
  product?: Product;
}

/**
 * Generic server-rendered add-to-basket button.
 * A block emits a `data-react-component="ProductAddToBasketButton"` mount point with a JSON
 * payload (code/quantity/label/className); this hydrates it and uses the shared useAddToBasket
 * hook so the mini-cart updates live. The optional className lets each block style the button
 * with its own class (e.g. N-FP-14 emits `nfp14__featured-cta`).
 *
 * The optional `product` payload is passed straight to useAddToBasket so the add-to-basket
 * confirmation toast (CartNotification) can render; without it the toast body is null and the
 * toast shows as an empty box. Mirrors the carousel's ProductTile, which passes its product.
 *
 * Generalised from CalloutAddToBasket (N-FP-04) so the shared API is not block-named.
 */
export const ProductAddToBasketButton = ({
  data,
}: {
  data: ProductAddToBasketButtonData;
}) => {
  const { code, quantity = 1, label, className = '', product } = data;
  const { addToBasket, isPending } = useAddToBasket(product);

  if (!code) {
    return null;
  }

  return (
    <button
      type="button"
      className={className}
      disabled={isPending}
      aria-busy={isPending}
      onClick={() => addToBasket({ code, quantity })}
    >
      <ShoppingCart />
      <span>{label}</span>
    </button>
  );
};
